import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { toast } from "react-toastify";
import { companyList } from "../../apis/api/DataApi";
import { updateMemberInvoice, getMemberInvoice } from "../../apis/api/InvoiceApi";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

const InvoiceEditModal = ({ show, handleClose, invoice }) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();
  const userId = Cookies.get("userID");
  const companyId = Cookies.get("company_id");

  const { CompanyList } = useSelector((state) => state.DataSlice);

  const [previewUrl, setPreviewUrl] = useState(invoice?.invoice_url || null);

  const invoiceStatusOptions = [
    { value: 1, label: "Paid" },
    { value: 0, label: "Unpaid" },
  ];

  const selectedStatus = watch("invoiceStatus");

  /* -------------------- LOAD COMPANY LIST -------------------- */
  useEffect(() => {
    dispatch(companyList());
  }, [dispatch]);

  /* -------------------- PREFILL FORM -------------------- */
  useEffect(() => {
    if (!invoice) return;
    reset({
      toCompany: CompanyList?.find(
        (c) => c.value === `${invoice.invoice_to}:${invoice.company_name}`
      ),
      invoiceStatus: invoiceStatusOptions.find(
        (s) => s.value === invoice.invoice_status
      ),
      amount: invoice.amount,
      dueDate: invoice.due_date ? new Date(invoice.due_date * 1000) : null,
      reminder: invoice.reminders?.toString(),
    });

    setPreviewUrl(invoice.invoice_url);
  }, [invoice, CompanyList, reset]);

  /* -------------------- SUBMIT -------------------- */
  const onSubmit = async (formDataRaw) => {
    try {
      const formData = new FormData();

      for (const key in formDataRaw) {
        let value = formDataRaw[key];

        if (typeof value === "object" && value?.value !== undefined) {
          value = value.value;
        }

        if (value instanceof Date) {
          value = value.toISOString();
        }

        if (value instanceof FileList) {
          if (value.length > 0) {
            formData.append(key, value[0]);
          }
          continue;
        }

        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      }

      formData.append("invoiceId", invoice.id);
      formData.append("companyId", companyId);
      formData.append("userId", userId);

      await dispatch(updateMemberInvoice(formData)).unwrap();
      await dispatch(getMemberInvoice({ companyId }));

      toast.success("Invoice updated successfully");
      handleClose();
    } catch (err) {
      toast.error("Failed to update invoice");
    }
  };

  return (
    <Modal show={show} size="lg" centered onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Invoice</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)} className="p-4 border rounded">
          <div className="row">

            {/* TO COMPANY */}
            <div className="col-md-6 mb-3">
              <Form.Label className="fw-bold">To Company *</Form.Label>
              <Controller
                name="toCompany"
                control={control}
                rules={{ required: "Company is required" }}
                render={({ field }) => <Select {...field} options={CompanyList} />}
              />
              {errors.toCompany && (
                <div className="text-danger">{errors.toCompany.message}</div>
              )}
            </div>

            {/* STATUS */}
            <div className="col-md-6 mb-3">
              <Form.Label className="fw-bold">Invoice Status *</Form.Label>
              <Controller
                name="invoiceStatus"
                control={control}
                rules={{ required: "Status is required" }}
                render={({ field }) => <Select {...field} options={invoiceStatusOptions} />}
              />
            </div>

            {/* AMOUNT */}
            <div className="col-md-6 mb-3">
              <Form.Label className="fw-bold">Amount (₹)</Form.Label>
              <Form.Control
                type="number"
                {...register("amount", { required: "Amount is required" })}
              />
              {errors.amount && <div className="text-danger">{errors.amount.message}</div>}
            </div>

            {/* DUE DATE & REMINDER: Only show if unpaid */}
            {selectedStatus?.value === 0 && (
              <>
                {/* DUE DATE */}
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Due Date *</Form.Label>
                  <Controller
                    name="dueDate"
                    control={control}
                    rules={{
                      required: "Due date is required for unpaid invoice",
                    }}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        selected={field.value}
                        onChange={field.onChange}
                        className={`form-control ${errors.dueDate ? "is-invalid" : ""}`}
                        dateFormat="dd/MM/yyyy"
                        minDate={new Date()}
                        placeholderText="Select due date"
                      />
                    )}
                  />
                  {errors.dueDate && <div className="text-danger">{errors.dueDate.message}</div>}
                </div>

                {/* REMINDER */}
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label className="fw-bold d-block">Send Reminder *</Form.Label>
                    <div className="d-flex gap-3">
                      <Form.Check
                        type="radio"
                        label="Yes"
                        value="1"
                        {...register("reminder", {
                          required: "Please select reminder option",
                        })}
                      />
                      <Form.Check
                        type="radio"
                        label="No"
                        value="0"
                        {...register("reminder", {
                          required: "Please select reminder option",
                        })}
                      />
                    </div>
                    {errors.reminder && (
                      <div className="text-danger">{errors.reminder.message}</div>
                    )}
                  </Form.Group>
                </div>
              </>
            )}

            {/* FILE UPLOAD */}
            <div className="col-12 mb-4">
              <Form.Label className="fw-bold">Replace Invoice File (Optional)</Form.Label>
              <Controller
                name="invoiceFile"
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Control
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
                          toast.error("Only PDF files are allowed!");
                          e.target.value = "";
                          field.onChange(null);
                          setPreviewUrl(invoice?.invoice_url || null);
                          return;
                        }
                        field.onChange(e.target.files);
                        if (file) setPreviewUrl(URL.createObjectURL(file));
                        else setPreviewUrl(invoice?.invoice_url || null);
                      }}
                    />
                    {previewUrl && (
                      <div className="mt-2">
                        <object
                          data={previewUrl}
                          type="application/pdf"
                          width="100%"
                          height="300px"
                        >
                          <p>
                            PDF preview not available.{" "}
                            <a href={previewUrl} target="_blank" rel="noreferrer">
                              Open PDF
                            </a>
                          </p>
                        </object>
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="text-end">
            <Button type="submit" variant="primary">
              Update Invoice
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default InvoiceEditModal;
